import { actionCreatorFactory, Action, Success, Failure } from 'typescript-fsa'
import { AppMetaData, CollectionItems, Collection, Screen, DevicePlatform, MediaItem } from './types'
import {
	AppToken,
	Manifest,
	GetMediaItemsShowResponse,
	RedeemCodeResponse,
	RedeemCode,
	MapRoute,
	Form,
} from 'mytours-read-api-typescript'

const actionCreator = actionCreatorFactory('Api')

// loads meta data about an app from network
export interface RequestParams {
	projectId?: string // Unused
	devicePlatform: DevicePlatform
	deviceUdid?: string // Unused
	onError?: (error: Error | Response) => void
}
export type LoadAppMetaDataFromNetworkParams = Action<RequestParams>
type LoadAppMetaDataFromNetworkSuccess = Success<RequestParams, AppMetaData>
export type LoadAppMetaDataFromNetworkResult = Action<LoadAppMetaDataFromNetworkSuccess>
type LoadAppMetaDataFromNetworkFailure = Failure<RequestParams, Error | Response>
export type LoadAppMetaDataFromNetworkResultFailure = Action<LoadAppMetaDataFromNetworkFailure>
export const fetchAppMetaData = actionCreator.async<RequestParams, AppMetaData, Error | Response>(
	'FETCH_APP_META_DATA_FROM_NETWORK',
)

// loads a collection with items from network
export interface CollectionItemsRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	collectionId: number
	collectionVersion: number
	collectionSubtype: 'list' | 'tour' | 'menu' // parent collection subtype - so we know where to store results on redux
	accountId?: number
	language: string
	page?: number
	perPage?: number
	isPreCaching: boolean // true if downloading item as part of the sync content feature
}
export type LoadCollectionItemsFromNetworkParams = Action<CollectionItemsRequestParams>
type LoadCollectionItemsFromNetworkSuccess = Success<CollectionItemsRequestParams, CollectionItems>
export type LoadCollectionItemsFromNetworkResult = Action<LoadCollectionItemsFromNetworkSuccess>
type LoadCollectionItemsFromNetworkFailure = Failure<CollectionItemsRequestParams, Error>
export type LoadCollectionItemsFromNetworkResultFailure = Action<LoadCollectionItemsFromNetworkFailure>
export const fetchCollectionItems = actionCreator.async<CollectionItemsRequestParams, CollectionItems, Error>(
	'FETCH_COLLECTION_ITEMS_FROM_NETWORK',
)

// loads a collection from network
export interface CollectionRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	collectionId: number
	collectionVersion: number
	accountId?: number
	language: string
	isPreCaching: boolean // true if downloading item as part of the sync content feature
}
export type LoadCollectionFromNetworkParams = Action<CollectionRequestParams>
type LoadCollectionFromNetworkSuccess = Success<CollectionRequestParams, Collection>
export type LoadCollectionFromNetworkResult = Action<LoadCollectionFromNetworkSuccess>
export const fetchCollection = actionCreator.async<CollectionRequestParams, Collection, Error>(
	'FETCH_COLLECTION_FROM_NETWORK',
)

// loads a screen from network
export interface ScreenRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	screenId: number
	screenVersion: number
	accountId?: number
	language: string
	isPreCaching: boolean // true if downloading item as part of the sync content feature
}
export type LoadScreenFromNetworkParams = Action<ScreenRequestParams>
type LoadScreenFromNetworkSuccess = Success<ScreenRequestParams, Screen>
export type LoadScreenFromNetworkResult = Action<LoadScreenFromNetworkSuccess>
type LoadScreenFromNetworkFailure = Failure<ScreenRequestParams, Error>
export type LoadScreenFromNetworkResultFailure = Action<LoadScreenFromNetworkFailure>
export const fetchScreen = actionCreator.async<ScreenRequestParams, Screen, Error>('FETCH_SCREEN_FROM_NETWORK')

export interface RentalTokenParams {
	appId: number
	token: string
}
export type ValidateRentalTokenParams = Action<RentalTokenParams>
export const validateRentalToken = actionCreator.async<RentalTokenParams, AppToken, Error>('VALIDATE_RENTAL_TOKEN')

export interface ManifestRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	isForSync: boolean // true if downloading manifest for syncing all content to device
	keypadNumber?: number // is set when we need to load screen details from the keypad entry
}
export type LoadManifestFromNetworkParams = Action<ManifestRequestParams>
type LoadManifestFromNetworkSuccess = Success<ManifestRequestParams, Manifest>
export type LoadManifestFromNetworkResult = Action<LoadManifestFromNetworkSuccess>
export const fetchManifest = actionCreator.async<ManifestRequestParams, Manifest, Error>('FETCH_MANIFEST_FROM_NETWORK')

// manifest was successfully loaded from disk to be put onto redux store
export const fetchManifestComplete = actionCreator<Manifest>('LOADED_MANIFEST_COMPLETE')

// loads a media item from network
export interface MediaItemRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	itemId: number
	itemVersion: number
	language: string
	isPreCaching: boolean
}
export type LoadMediaItemFromNetworkParams = Action<MediaItemRequestParams>
type LoadMediaItemFromNetworkSuccess = Success<MediaItemRequestParams, MediaItem>
export type LoadMediaItemFromNetworkResult = Action<LoadMediaItemFromNetworkSuccess>
type LoadMediaItemFromNetworkFailure = Failure<MediaItemRequestParams, Error>
export type LoadMediaItemFromNetworkResultFailure = Action<LoadMediaItemFromNetworkFailure>
export const fetchMediaItem = actionCreator.async<MediaItemRequestParams, GetMediaItemsShowResponse, Error>(
	'FETCH_MEDIA_ITEM_FROM_NETWORK',
)

// loads a form from network
export interface FormRequestParams {
	id: number
	version: number
	language: string
	isPreCaching: boolean
}
export type LoadFormFromNetworkParams = Action<FormRequestParams>
type LoadFormFromNetworkSuccess = Success<FormRequestParams, Form>
export type LoadFormFromNetworkResult = Action<LoadFormFromNetworkSuccess>
type LoadFormFromNetworkFailure = Failure<FormRequestParams, Error>
export type LoadFormFromNetworkResultFailure = Action<LoadFormFromNetworkFailure>
export const fetchForm = actionCreator.async<FormRequestParams, Form, Error>(
	'FETCH_FORM_FROM_NETWORK',
)

// loads map route from network
export interface MapRouteRequestParams {
	devicePlatform: DevicePlatform
	deviceUdid: string
	linkedId: number
	linkedType: MapRoute.LinkedTypeEnum
	mapRouteId: number
	mapRouteVersion: number
	language: string
	accountId?: number
}
export type LoadMapRouteFromNetworkParams = Action<MapRouteRequestParams>
type LoadMapRouteFromNetworkSuccess = Success<MapRouteRequestParams, MapRoute>
export type LoadMapRouteFromNetworkResult = Action<LoadMapRouteFromNetworkSuccess>
type LoadMapRouteFromNetworkFailure = Failure<MapRouteRequestParams, Error>
export type LoadMapRouteFromNetworkResultFailure = Action<LoadMapRouteFromNetworkFailure>
export const fetchMapRoute = actionCreator.async<MapRouteRequestParams, MapRoute, Error>('FETCH_MAP_ROUTE_FROM_NETWORK')

export interface CouponCodeRequestParams {
	linkedId: number
	linkedType: RedeemCode.LinkedTypeEnum
	devicePlatform: DevicePlatform
	deviceUdid: string
	couponCode: string
	email?: string
}
export type LoadCouponCodeParams = Action<CouponCodeRequestParams>

type LoadCouponCodeSuccess = Success<CouponCodeRequestParams, RedeemCodeResponse>
export type LoadCouponCodeResultSuccess = Action<LoadCouponCodeSuccess>

export interface ValidateCodeParams {
	linkedId: number
	linkedType: RedeemCode.LinkedTypeEnum
	devicePlatform: DevicePlatform
	deviceUdid: string
	couponCode: string
	expireAt?: number
	onComplete?: (isValid: boolean) => void
}
export type ValidateCodeAction = Action<ValidateCodeParams>

type ValidateCodeSuccess = Success<ValidateCodeParams, RedeemCodeResponse>
export type ValidateCodeResultSuccess = Action<ValidateCodeSuccess>
type ValidateCodeFailure = Failure<ValidateCodeParams, Error>
export type ValidateCodeResultFailure = Action<ValidateCodeFailure>

export const validateCode = actionCreator.async<ValidateCodeParams, RedeemCodeResponse, Error>(
	'COUPON_VALIDATE_APP_CODE',
)
export const redeemCode = actionCreator.async<CouponCodeRequestParams, RedeemCodeResponse, Error>(
	'COUPON_REDEEM_APP_CODE',
)
